import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isFileObject } from "../../utils/validation";
import { message } from "antd";
import { DocCategoryService } from "../../services/docCategoryService";
import { DocCategoryDetail, DocCategoryFormData, DocCategoryListItem, defaultDocCategoryData, OperationStatus } from "./constants";

interface DocCategoryContextState {
	// Core Domain Data
	docCategoryList: DocCategoryListItem[];
	docCategoryDetail: DocCategoryDetail | null;
	docCategoryForm: DocCategoryFormData;
	operationStatus: OperationStatus;

	// Setters
	setDocCategoryForm: React.Dispatch<React.SetStateAction<DocCategoryFormData>>;
	clearDocCategoryData: () => void;

	// Actions
	addDocCategory: () => Promise<void>;
	getDocCategoryList: () => Promise<void>;
	updateDocCategory: () => Promise<void>;
	removeDocCategory: (id: string) => Promise<void>;
	getDocCategoryDetails: (id: string) => Promise<void>;
}

interface DocCategoryProviderProps {
	children: React.ReactNode;
}

const DocCategoryContext = createContext<DocCategoryContextState | undefined>(
	undefined
);

export const useDocCategoryContext = () => {
	const context = useContext(DocCategoryContext);
	if (!context) {
		throw new Error(
			"useDocumentCategoryContext must be used within an DocumentCategoryContextProvider"
		);
	}
	return context;
};

export const DocCategoryContextProvider: React.FC<DocCategoryProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [docCategoryList, setDocCategoryList] = useState<DocCategoryListItem[]>([]);
	const [docCategoryDetail, setDocCategoryDetail] = useState<DocCategoryDetail | null>(null);
	const [docCategoryForm, setDocCategoryForm] = useState<DocCategoryFormData>(defaultDocCategoryData);

	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });

	const isDocCategoryDataValid = (data: DocCategoryFormData) => {
		if (!data.name) {
			return false;
		}

		for (const document of data.documents || []) {
			if (!document && !isFileObject(document)) {
				return false;
			}
		}
		return true;
	};

	const clearDocCategoryData = () => {
		setDocCategoryForm(defaultDocCategoryData);
		setDocCategoryDetail(null);
	};

	const getDocCategoryList = async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await DocCategoryService.getDocCategoryList();

			if (response.isSuccessful) {
				setDocCategoryList(response.result?.items || []);
				clearDocCategoryData();
			} else {
				message.error(response.error ? response.error : "Belge kategorisi listesi yüklenirken hata oluştu.");
				setDocCategoryList([]);
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Belge kategorisi listesi yüklenirken hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const addDocCategory = async () => {
		setOperationStatus({ type: "loading", operation: "add" });

		if (!isDocCategoryDataValid(docCategoryForm)) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await DocCategoryService.addDocCategory(docCategoryForm);

			if (response.data?.result?.isSuccessful) {
				message.success("Belge kategorisi başarıyla eklendi.");
				setDocCategoryForm(defaultDocCategoryData);
				navigate("/doc-categories");
			} else {
				message.error(response.data.result?.error?.message || "Beklenmedik Bir Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "add", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}	
	};

	const getDocCategoryDetails = async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });
		
		try {
			const response = await DocCategoryService.getDocCategoryDetails(id);

			if (response.isSuccessful) {
				setDocCategoryDetail({
					categoryId: response.result.categoryId,
					categoryNo: response.result.categoryNo,
					name: response.result.name,
				});

				setDocCategoryForm(prevState => ({
					...prevState,
					categoryId: response.result.categoryId,
					categoryNo: response.result.categoryNo,
					name: response.result.name,
				}));
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const removeDocCategory = async (id: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await DocCategoryService.removeDocCategory(id);
			if (response.data?.result?.isSuccessful) {
				message.success("Belge kategorisi başarıyla silindi.");
				await getDocCategoryList();
			} else {
				message.error(response.data?.result?.error ? response.data.result.error.message : "Belge kategorisi silinirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "delete", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	const updateDocCategory = async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!isDocCategoryDataValid(docCategoryForm) || !docCategoryForm.categoryId) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		const documentsChanged = docCategoryForm.documents && docCategoryForm.documents.length > 0;

		if (!documentsChanged) {
			message.info("Değişiklik yapılmadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await DocCategoryService.updateDocCategory(docCategoryForm);

			if (response.data.isSuccessful) {
				message.success("Belge kategorisi başarıyla güncellendi.");
				clearDocCategoryData();
				navigate("/doc-categories");
			} else {
				message.error(response.data.error?.message || "Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	};

	return (
		<DocCategoryContext.Provider
			value={{
				docCategoryForm,
				docCategoryList,
				docCategoryDetail,
				setDocCategoryForm,
				operationStatus,
				addDocCategory,
				removeDocCategory,
				updateDocCategory,
				getDocCategoryList,
				getDocCategoryDetails,
				clearDocCategoryData,
			}}
		>
			{children}
		</DocCategoryContext.Provider>
	);
};